import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import { useLocation, useSearchParams } from 'react-router';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { HeadlineM } from '@sorare/core/src/atoms/typography';
import { DivisionLogo } from '@sorare/core/src/components/leaderboard/DivisionLogo';
import { ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { MissingCardsMessage } from '@sorare/gameplay/src/components/unlockCompetition/MissingCardsMessage';

import type {
  MarketLeaderboardQuery,
  MarketLeaderboardQueryVariables,
} from './__generated__/index.graphql';

const StyledLink = styled(Link)`
  display: grid;
  grid-template-areas:
    'logo overline'
    'logo title';
  grid-template-columns: max-content 1fr;
  column-gap: var(--double-unit);
  padding: var(--double-unit);
  border: 1px solid var(--c-nd-100);
  background-color: var(--c-nd-50);
  border-radius: var(--double-unit);
  align-items: center;
  &,
  &:hover,
  &:focus {
    color: var(--c-white);
  }
  @media ${laptopAndAbove} {
    grid-template-areas:
      'logo overline cta'
      'logo title cta';
    column-gap: var(--double-unit);
    grid-template-columns: max-content 1fr max-content;
  }
`;
const DivisionLogoWrapper = styled.div`
  grid-area: logo;
`;
const Overline = styled.p`
  font: var(--t-14-16);
  grid-area: overline;
  color: var(--c-nd-700);
`;
const Title = styled(HeadlineM)`
  grid-area: title;
`;
const StyledButton = styled(Button)`
  display: none;
  @media ${laptopAndAbove} {
    display: flex;
    grid-area: cta;
  }
`;

const MARKET_LEADERBOARD_QUERY = gql`
  query MarketLeaderboardQuery($slug: String!) {
    so5 {
      so5Leaderboard(slug: $slug) {
        slug
        displayName
        seasonality
        so5Fixture {
          slug
          type
        }
        so5League {
          slug
        }
        ...MissingCardsMessage_so5Leaderboard
        ...DivisionLogo_so5Leaderboard
      }
    }
  }
  ${DivisionLogo.fragments.so5Leaderboard}
  ${MissingCardsMessage.fragments.so5Leaderboard}
` as TypedDocumentNode<MarketLeaderboardQuery, MarketLeaderboardQueryVariables>;

export const SelectedLeaderboardBanner = () => {
  const [searchParams] = useSearchParams();
  const leaderboardSlug = searchParams.get('leaderboard');
  const { generateSportContextPath } = useSportContext();

  const { search } = useLocation();

  const { data } = useQuery(MARKET_LEADERBOARD_QUERY, {
    variables: { slug: leaderboardSlug || '' },
    skip: !leaderboardSlug,
  });

  if (!data) return null;

  const { so5Leaderboard } = data.so5;

  return (
    <StyledLink
      to={`${generateSportContextPath(
        ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_COMPETITION_COMPETITION,
        {
          params: {
            fixture: so5Leaderboard.so5Fixture.slug,
            eventType: so5Leaderboard.so5Fixture.type.toLowerCase(),
            competition: so5Leaderboard.so5League.slug,
          },
        }
      )}${search}`}
    >
      <DivisionLogoWrapper>
        <DivisionLogo so5Leaderboard={so5Leaderboard} />
      </DivisionLogoWrapper>
      <Overline>{so5Leaderboard.displayName}</Overline>
      <Title as="h4">
        <MissingCardsMessage so5Leaderboard={so5Leaderboard} />
      </Title>
      <StyledButton color="tertiary" size="medium" asSpan>
        <FormattedMessage
          id="TransferMarket.NewSignings.RecommendedLeaderboardBanner.Cta"
          defaultMessage="Competition requirements"
        />
      </StyledButton>
    </StyledLink>
  );
};
